import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, Crown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { track, flushBeforeRedirect } from "@/lib/analytics";

const PLANS = [
  {
    id: "monthly",
    label: "Monthly",
    price: "$7.99",
    cadence: "/month",
    note: "Renews monthly · cancel anytime",
    highlight: false,
  },
  {
    id: "annual",
    label: "Annual",
    price: "$59.99",
    cadence: "/year",
    note: "Billed yearly · renews annually · save 37%",
    highlight: true,
  },
];

const PERKS = [
  "12 full AI readings every month",
  "Daily Ritual + up to 3 follow-up questions per reading",
  "Save, revisit & share every reading",
];

export const UpgradeModal = ({ open, onOpenChange }) => {
  const { user, login } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const subscribe = async (plan) => {
    if (!user) {
      toast.info("Sign in first to upgrade.");
      login();
      return;
    }
    setLoadingPlan(plan);
    try {
      const res = await api.post("/payments/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      track("checkout_started", { plan_interval: plan });
      await flushBeforeRedirect();
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not open checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-[#D9A441]/30 bg-[#0B0C15] text-[#F8F3E6]"
        data-testid="upgrade-modal"
      >
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D9A441]/50 bg-[#131422]">
              <Crown className="h-4 w-4 text-[#D9A441]" />
            </span>
            <DialogTitle className="font-display text-2xl font-semibold text-[#F8F3E6]">
              The Cardlight Premium
            </DialogTitle>
          </div>
          <DialogDescription className="text-[#A3A5B8]">
            Unlock a full month of readings and deeper guidance.
          </DialogDescription>
        </DialogHeader>

        <ul className="my-2 space-y-2.5">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm text-[#F8F3E6]">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D9A441]" />
              {p}
            </li>
          ))}
        </ul>

        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              onClick={() => subscribe(plan.id)}
              disabled={loadingPlan !== null}
              data-testid={`upgrade-${plan.id}-btn`}
              className={`relative flex flex-col items-start gap-1 rounded-2xl border p-5 text-left transition-all disabled:opacity-60 ${
                plan.highlight
                  ? "border-[#D9A441]/70 bg-[#D9A441]/10 hover:shadow-[0_0_28px_rgba(217,164,65,0.3)]"
                  : "border-[#D9A441]/25 bg-[#131422]/70 hover:border-[#D9A441]/50"
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-2.5 right-4 rounded-full bg-[#D9A441] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0B0C15]">
                  Best value
                </span>
              )}
              <span className="text-xs uppercase tracking-[0.2em] text-[#A3A5B8]">
                {plan.label}
              </span>
              <span className="flex items-baseline gap-1">
                <span className="font-display text-3xl font-semibold text-[#F8F3E6]">
                  {plan.price}
                </span>
                <span className="text-sm text-[#A3A5B8]">{plan.cadence}</span>
              </span>
              <span className="text-xs text-[#E5C365]">{plan.note}</span>
              <span className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#D9A441] px-4 py-2 text-sm font-medium text-[#0B0C15]">
                {loadingPlan === plan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {loadingPlan === plan.id ? "Opening…" : "Choose plan"}
              </span>
            </button>
          ))}
        </div>

        <p className="mt-3 text-center text-xs leading-relaxed text-[#A3A5B8]">
          Renews automatically. Cancel anytime; access continues through your paid
          period. No prorated refunds, except where required by law. Billing errors:
          support@thecardlight.com.{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#E5C365] underline underline-offset-4"
          >
            Read subscription terms
          </a>
        </p>
        <p className="mt-2 text-center text-xs text-[#A3A5B8]">
          Secure checkout by Stripe
        </p>
      </DialogContent>
    </Dialog>
  );
};
